"use client";

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { useRouter } from "@/i18n/navigation";
import type { User } from "@supabase/supabase-js";
import { afterInitialPaint } from "@/lib/after-initial-paint";
import { PendingSubscription } from "@/lib/types";
import { AuthModal } from "./auth-modal";
import { SubscriptionConfirmModal } from "./subscription-confirm-modal";

let authClient: Promise<ReturnType<typeof import("@/lib/supabase/client").createClient>> | undefined;
function loadAuthClient() {
  return authClient ??= import("@/lib/supabase/client").then(({ createClient }) => createClient());
}

interface AuthContextValue {
  user: User | null;
  isLoading: boolean;
  isAdmin: boolean;
  avatarUrl: string | null;
  setAvatarUrl: (url: string) => void;
  subscribedArtistIds: Set<number>;
  subscribedVenueIds: Set<number>;
  subscribedEventIds: Set<number>;
  subscribe: (
    type: "artist" | "venue" | "show",
    targetId: number,
    targetName: string
  ) => Promise<void>;
  unsubscribe: (type: "artist" | "venue" | "show", targetId: number) => Promise<void>;
  openAuthModal: (pending: PendingSubscription[]) => void;
}

const AuthContext = createContext<AuthContextValue>({
  user: null,
  isLoading: true,
  isAdmin: false,
  avatarUrl: null,
  setAvatarUrl: () => {},
  subscribedArtistIds: new Set(),
  subscribedVenueIds: new Set(),
  subscribedEventIds: new Set(),
  subscribe: async () => {},
  unsubscribe: async () => {},
  openAuthModal: () => {},
});

export function AuthProvider({
  children,
  initialUser = null,
}: {
  children: React.ReactNode;
  initialUser?: User | null;
}) {
  const [user, setUser] = useState<User | null>(initialUser);
  const [isLoading, setIsLoading] = useState(!initialUser);
  const [subscribedArtistIds, setSubscribedArtistIds] = useState<Set<number>>(
    new Set()
  );
  const [subscribedVenueIds, setSubscribedVenueIds] = useState<Set<number>>(
    new Set()
  );
  const [subscribedEventIds, setSubscribedEventIds] = useState<Set<number>>(
    new Set()
  );
  const [isAdmin, setIsAdmin] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [pendingSubscriptions, setPendingSubscriptions] =
    useState<PendingSubscription[] | null>(null);
  const [confirmedSubscription, setConfirmedSubscription] = useState<{
    name: string;
    type: "artist" | "venue" | "show";
  } | null>(null);

  const router = useRouter();

  // Check for ?subscribed=Name&subscribedType=artist after auth redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const name = params.get("subscribed");
    const type = params.get("subscribedType");
    if (name && (type === "artist" || type === "venue" || type === "show")) {
      setConfirmedSubscription({ name, type });
      // Clean the URL
      params.delete("subscribed");
      params.delete("subscribedType");
      const qs = params.toString();
      router.replace(`${window.location.pathname}${qs ? `?${qs}` : ""}`, {
        scroll: false,
      });
    }
  }, [router]);

  const loadSubscriptions = useCallback(
    async (userId: string) => {
      const supabase = await loadAuthClient();
      const [{ data }, { data: profile }] = await Promise.all([
        supabase.rpc("get_user_subscription_ids"),
        supabase.from("profiles").select("is_admin, avatar_url").eq("id", userId).single(),
      ]);
      setIsAdmin(!!profile?.is_admin);
      setAvatarUrl(profile?.avatar_url ?? null);
      if (data) {
        const artists = new Set<number>();
        const venues = new Set<number>();
        const events = new Set<number>();
        for (const row of data as {
          subscription_type: string;
          target_id: number;
        }[]) {
          if (row.subscription_type === "artist") artists.add(row.target_id);
          else if (row.subscription_type === "venue") venues.add(row.target_id);
          else if (row.subscription_type === "show") events.add(row.target_id);
        }
        setSubscribedArtistIds(artists);
        setSubscribedVenueIds(venues);
        setSubscribedEventIds(events);
      }
    },
    []
  );

  useEffect(() => {
    let disposed = false;
    let unsubscribe: (() => void) | undefined;
    const connect = async () => {
      const supabase = await loadAuthClient();
      if (disposed) return;
      let initialized = false;

      // If we have an initial user, load their subscriptions.
      if (initialUser) {
        loadSubscriptions(initialUser.id);
        initialized = true;
      }

      const {
        data: { subscription },
      } = supabase.auth.onAuthStateChange(async (event, session) => {
        try {
          const newUser = session?.user ?? null;

          if (newUser && !newUser.email_confirmed_at) {
            // Unconfirmed email: treat as unauthenticated in UI.
            setUser(null);
            setIsAdmin(false);
            setAvatarUrl(null);
            setSubscribedArtistIds(new Set());
            setSubscribedVenueIds(new Set());
            setSubscribedEventIds(new Set());
          } else if (newUser) {
            setUser(newUser);
            loadSubscriptions(newUser.id);
          } else {
            setUser(null);
            setIsAdmin(false);
            setAvatarUrl(null);
            setSubscribedArtistIds(new Set());
            setSubscribedVenueIds(new Set());
            setSubscribedEventIds(new Set());
          }
        } catch (e) {
          console.error("Auth state change error:", e);
        } finally {
          if (!initialized) {
            initialized = true;
            setIsLoading(false);
          }
        }
      });

      unsubscribe = () => subscription.unsubscribe();
    };
    // Anonymous readers can paint the server-rendered feed before downloading
    // the auth SDK. Existing server sessions still connect immediately.
    if (initialUser) void connect().catch(() => setIsLoading(false));
    else afterInitialPaint(() => {
      if (!disposed) void connect().catch(() => setIsLoading(false));
    });
    return () => { disposed = true; unsubscribe?.(); };
  }, [loadSubscriptions, initialUser]);

  const subscribe = useCallback(
    async (type: "artist" | "venue" | "show", targetId: number) => {
      if (!user) return;
      const supabase = await loadAuthClient();

      // Optimistic update
      if (type === "artist") {
        setSubscribedArtistIds((prev) => new Set(prev).add(targetId));
      } else if (type === "venue") {
        setSubscribedVenueIds((prev) => new Set(prev).add(targetId));
      } else {
        setSubscribedEventIds((prev) => new Set(prev).add(targetId));
      }

      const row =
        type === "artist"
          ? {
              user_id: user.id,
              subscription_type: "artist" as const,
              attraction_id: targetId,
            }
          : type === "venue"
            ? {
                user_id: user.id,
                subscription_type: "venue" as const,
                venue_id: targetId,
              }
            : {
                user_id: user.id,
                subscription_type: "show" as const,
                event_id: targetId,
              };

      const { error } = await supabase.from("subscriptions").insert<typeof row>(row);
      if (error) {
        // Revert on failure
        if (type === "artist") {
          setSubscribedArtistIds((prev) => {
            const next = new Set(prev);
            next.delete(targetId);
            return next;
          });
        } else if (type === "venue") {
          setSubscribedVenueIds((prev) => {
            const next = new Set(prev);
            next.delete(targetId);
            return next;
          });
        } else {
          setSubscribedEventIds((prev) => {
            const next = new Set(prev);
            next.delete(targetId);
            return next;
          });
        }
      }
    },
    [user]
  );

  const unsubscribe = useCallback(
    async (type: "artist" | "venue" | "show", targetId: number) => {
      if (!user) return;
      const supabase = await loadAuthClient();

      // Optimistic update
      if (type === "artist") {
        setSubscribedArtistIds((prev) => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
      } else if (type === "venue") {
        setSubscribedVenueIds((prev) => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
      } else {
        setSubscribedEventIds((prev) => {
          const next = new Set(prev);
          next.delete(targetId);
          return next;
        });
      }

      const query = supabase
        .from("subscriptions")
        .delete()
        .eq("user_id", user.id);

      if (type === "artist") {
        query.eq("attraction_id", targetId);
      } else if (type === "venue") {
        query.eq("venue_id", targetId);
      } else {
        query.eq("event_id", targetId);
      }

      const { error } = await query;
      if (error) {
        // Revert on failure
        if (type === "artist") {
          setSubscribedArtistIds((prev) => new Set(prev).add(targetId));
        } else if (type === "venue") {
          setSubscribedVenueIds((prev) => new Set(prev).add(targetId));
        } else {
          setSubscribedEventIds((prev) => new Set(prev).add(targetId));
        }
      }
    },
    [user]
  );

  const openAuthModal = useCallback((pending: PendingSubscription[]) => {
    setPendingSubscriptions(pending);
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAdmin,
        avatarUrl,
        setAvatarUrl,
        subscribedArtistIds,
        subscribedVenueIds,
        subscribedEventIds,
        subscribe,
        unsubscribe,
        openAuthModal,
      }}
    >
      {children}
      {pendingSubscriptions && pendingSubscriptions.length > 0 && (
        <AuthModal
          pending={pendingSubscriptions}
          onClose={() => setPendingSubscriptions(null)}
        />
      )}
      {confirmedSubscription && (
        <SubscriptionConfirmModal
          name={confirmedSubscription.name}
          type={confirmedSubscription.type}
          onClose={() => setConfirmedSubscription(null)}
        />
      )}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  return useContext(AuthContext);
}
